import { useState, useEffect, useMemo, useCallback } from "react";
import type { SubscriptionV2Variable } from "@/pages/subscriptions/services/types";
import type { MetricDefinition } from "@/pages/subscriptions/services/types";
import type { ScheduleValue } from "../types";
import { VariableType, ChangeType, ComparisonType } from "@/pages/subscriptions/constants";

interface UseVariableEditorStateProps {
  variable: SubscriptionV2Variable;
  open?: boolean;
  metricDefinition?: MetricDefinition | null;
  allVariables?: SubscriptionV2Variable[];
}

interface UseVariableEditorStateReturn {
  // Basic state
  value: string | string[];
  displayValue: string;
  validationError: string;
  setValue: (val: string | string[]) => void;
  setDisplayValue: (val: string) => void;
  setValidationError: (error: string) => void;

  // Threshold state
  thresholdType: "amount" | "percentage";
  thresholdValue: string;
  isAbsoluteValue: boolean;
  setThresholdType: (type: "amount" | "percentage") => void;
  setThresholdValue: (value: string) => void;

  // Schedule state
  scheduleData: ScheduleValue;
  setScheduleData: (data: ScheduleValue) => void;

  // Computed
  filteredOptions: Array<{ value: string; label: string }>;
  isEditable: boolean;
  unit: string;

  // Validation
  validateThreshold: (val: string, type: "amount" | "percentage") => boolean;
  validateSchedule: () => boolean;

  // Formatting
  formatScheduleDisplay: (data: ScheduleValue) => string;
}

export const useVariableEditorState = ({
  variable,
  open,
  metricDefinition,
  allVariables = [],
}: UseVariableEditorStateProps): UseVariableEditorStateReturn => {
  const [value, setValue] = useState<string | string[]>(variable.value);
  const [displayValue, setDisplayValue] = useState(variable.displayValue);
  const [validationError, setValidationError] = useState<string>("");

  // Check if change_type is absolute_value (percentage not allowed for threshold)
  const isAbsoluteValue = useMemo(() => {
    const changeTypeVar = allVariables.find(v => v.type === VariableType.CHANGE_TYPE);
    return changeTypeVar?.value === ChangeType.ABSOLUTE_VALUE;
  }, [allVariables]);

  // Schedule-specific state
  const [scheduleData, setScheduleData] = useState<ScheduleValue>(() => {
    if (variable.type === VariableType.SCHEDULE && typeof variable.value === "string") {
      try {
        return JSON.parse(variable.value);
      } catch {
        return { frequency: "daily", time: "2:00 PM" };
      }
    }
    return { frequency: "daily", time: "2:00 PM" };
  });

  // Threshold-specific state
  const [thresholdType, setThresholdType] = useState<"amount" | "percentage">(() => {
    if (variable.type === VariableType.THRESHOLD) {
      const comparisonVar = allVariables.find(v => v.type === VariableType.COMPARISON);
      if (comparisonVar?.value === ComparisonType.CHANGES_BY_PERCENT) {
        return "percentage";
      }
      if (typeof variable.value === "string") {
        return variable.value.includes("%") ? "percentage" : "amount";
      }
    }
    return "amount";
  });

  const [thresholdValue, setThresholdValue] = useState<string>(() => {
    if (variable.type === VariableType.THRESHOLD && typeof variable.value === "string") {
      return String(variable.value).replace(/[$%,]/g, "");
    }
    return "";
  });

  // Check if variable is editable
  const isEditable = variable.config?.isEditable !== false;

  // Get unit from metric definition
  const unit = metricDefinition?.unit || "";

  // Filter options based on metric definition capabilities
  const filteredOptions = useMemo(() => {
    if (!variable.config?.options || !metricDefinition) {
      return variable.config?.options || [];
    }

    const options = variable.config.options;

    // For aggregation type, filter based on supports_aggregations
    if (variable.type === VariableType.AGGREGATION && metricDefinition.supports_aggregations) {
      return options.filter((opt: { value: string }) =>
        metricDefinition.supports_aggregations.includes(opt.value)
      );
    }

    // For comparison type (threshold comparisons), filter based on supports_threshold_types
    if (variable.type === VariableType.COMPARISON && metricDefinition.supports_threshold_types) {
      return options.filter((opt: { value: string }) =>
        metricDefinition.supports_threshold_types.includes(opt.value)
      );
    }

    // For change_type, filter based on supports_comparison_types
    if (variable.type === VariableType.CHANGE_TYPE && metricDefinition.supports_comparison_types) {
      return options.filter((opt: { value: string }) =>
        metricDefinition.supports_comparison_types.includes(opt.value)
      );
    }

    // For date_range type, filter based on supports_date_ranges
    if (variable.type === VariableType.DATE_RANGE && metricDefinition.supports_date_ranges) {
      return options.filter((opt: { value: string }) =>
        metricDefinition.supports_date_ranges.includes(opt.value)
      );
    }

    return options;
  }, [variable.config?.options, variable.type, metricDefinition]);

  // Reset local state when variable changes or popover opens
  useEffect(() => {
    if (open) {
      setValue(variable.value);
      setDisplayValue(variable.displayValue);
      setValidationError("");

      if (variable.type === VariableType.THRESHOLD && typeof variable.value === "string") {
        const val = String(variable.value);
        const cleanVal = val.replace(/[$%,]/g, "");
        setThresholdValue(cleanVal);
        setThresholdType(val.includes("%") ? "percentage" : "amount");
      }

      if (variable.type === VariableType.SCHEDULE && typeof variable.value === "string") {
        try {
          setScheduleData(JSON.parse(variable.value));
        } catch {
          setScheduleData({ frequency: "daily", time: "2:00 PM" });
        }
      }
    }
  }, [open, variable]);

  const validateThreshold = useCallback((val: string, type: "amount" | "percentage"): boolean => {
    if (!val || val.trim() === "") {
      setValidationError(type === "amount" ? "Amount is required" : "Percentage is required");
      return false;
    }
    const numVal = parseFloat(val);
    if (isNaN(numVal)) {
      setValidationError("Must be a valid number");
      return false;
    }
    if (numVal <= 0) {
      setValidationError(
        type === "amount" ? "Amount must be greater than 0" : "Percentage must be greater than 0"
      );
      return false;
    }
    if (type === "percentage" && numVal > 100) {
      setValidationError("Percentage must be 100 or less");
      return false;
    }
    setValidationError("");
    return true;
  }, []);

  const validateSchedule = useCallback((): boolean => {
    if (
      scheduleData.frequency === "weekly" &&
      (!scheduleData.daysOfWeek || scheduleData.daysOfWeek.length === 0)
    ) {
      setValidationError("Please select at least one day of the week");
      return false;
    }
    if (scheduleData.frequency === "monthly" && !scheduleData.dayOfMonth) {
      setValidationError("Please select a day of the month");
      return false;
    }
    setValidationError("");
    return true;
  }, [scheduleData]);

  const formatScheduleDisplay = useCallback((data: ScheduleValue): string => {
    const { frequency, time, daysOfWeek, dayOfMonth } = data;

    if (frequency === "daily") {
      return `Daily at ${time}`;
    }

    if (frequency === "weekly" && daysOfWeek && daysOfWeek.length > 0) {
      const daysStr = daysOfWeek.join(", ");
      return `Weekly on ${daysStr} at ${time}`;
    }

    if (frequency === "monthly" && dayOfMonth) {
      return `Monthly on day ${dayOfMonth} at ${time}`;
    }

    return `${frequency.charAt(0).toUpperCase() + frequency.slice(1)} at ${time}`;
  }, []);

  return {
    // Basic state
    value,
    displayValue,
    validationError,
    setValue,
    setDisplayValue,
    setValidationError,

    // Threshold state
    thresholdType,
    thresholdValue,
    isAbsoluteValue,
    setThresholdType,
    setThresholdValue,

    // Schedule state
    scheduleData,
    setScheduleData,

    // Computed
    filteredOptions,
    isEditable,
    unit,

    // Validation
    validateThreshold,
    validateSchedule,

    // Formatting
    formatScheduleDisplay,
  };
};
